import { GridColDef } from '@mui/x-data-grid-premium';
import { AxiosRequestConfig } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useInitialTableSettings, useCompleteTableSettings } from 'src/core/tables/useTableSettings';
import tableHelper from 'src/core/tables/tableHelper';
import { useCurrencies } from 'src/pages/settings/settingsRequests';
import useRequest from 'src/core/api/useRequest';
import { buildCurrencyRequest } from 'src/core/forms/submit/submitRequests';
import { getCurrencies } from 'src/core/api/apiRequests';
import { useMessageContext } from 'src/shared/support/MessageContext';
import type { CurrencyUpdatePayload, CurrencyUpdateValues } from 'src/core/forms/submit/mappings';

export const useCurrenciesTable = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const { rows, status, loading } = useCurrencies(refreshKey);
  const [savedUpdates, setSavedUpdates] = useState<Record<number, CurrencyUpdatePayload>>({});
  const [refreshing, setRefreshing] = useState(false);
  const refreshStarted = useRef(false);
  const displayRows = useMemo(() => rows?.map(row => savedUpdates[row.Currency.Key] ? { ...row, ...savedUpdates[row.Currency.Key] } : row), [rows, savedUpdates]);
  const initialTableSettings = useInitialTableSettings({ tableName: 'CurrenciesTable', idPath: ['Currency', 'Key'], status, disableSelectionOnClick: true }, displayRows);
  const batch = useCurrencySubmit(status === 'success' && !loading && !refreshing, (id, payload) => {
    setSavedUpdates(previous => ({ ...previous, [id]: payload }));
    initialTableSettings.discardRowChanges(id);
  }, () => {
    refreshStarted.current = false;
    setRefreshing(true);
    setRefreshKey(previous => previous + 1);
  });
  const saving = batch.saving || refreshing;
  const canEdit = status === 'success' && !loading && !saving;
  const columns: GridColDef[] = useMemo(() => [
    // Re-enable row discard and the two editable flags when currency updates are supported.
    // { field: 'actions', getActions: (params, index) => saving ? [] : [tableHelper.DiscardChangesButton(initialTableSettings, params, index)], type: 'actions', headerName: '', width: 40 },
    { field: 'Enabled', headerName: 'Enabled', type: 'boolean' },
    { field: 'Currency_Value', headerName: 'Currency', valueGetter: tableHelper.getFieldKeyOrValue },
    { field: 'Description', headerName: 'Description', /* editable: canEdit, */ flex: 1 },
    { field: 'FixedRate', headerName: 'Fixed Rate', /* editable: canEdit, */ type: 'number' },
    { field: 'FixedRateByUser_Value', headerName: 'Fixed Rate By User', valueGetter: tableHelper.getFieldKeyOrValue },
    { field: 'FixedRateValidFrom', headerName: 'Fixed Rate Valid From', type: 'dateTime', valueGetter: (value: string) => value?.startsWith('0001-01-01') ? null : tableHelper.getDateColumn(value) },
    { field: 'ModifyDate', headerName: 'Modify Date', type: 'dateTime', valueGetter: tableHelper.getDateColumn },
  ], [initialTableSettings, saving, canEdit]);
  const tableSettings = useCompleteTableSettings({ columns }, initialTableSettings);

  useEffect(() => {
    if (!refreshing) return;
    if (status === 'loading' || status === 'retry') {
      refreshStarted.current = true;
      return;
    }
    if (!refreshStarted.current || loading) return;
    if (status === 'success') {
      setSavedUpdates({});
      setRefreshing(false);
    } else if (status === 'error' || status === 'aborted' || status === 204 || typeof status === 'number' && status >= 400) {
      setRefreshing(false);
    }
  }, [refreshing, status, loading]);

  tableSettings.loading = loading;
  return [columns, tableSettings, { submit: batch.submit, saving }] as const;
};

const useCurrencySubmit = (canSave: boolean, onSaved: (id: number, payload: CurrencyUpdatePayload) => void, onFinished: () => void) => {
  const [, setRequest, , loading, status] = useRequest({ logLevel: ['error', 'success'] });
  const [saving, setSaving] = useState(false);
  const { updateMessageStatus } = useMessageContext();
  const reportValidationError = (message: string) => updateMessageStatus({ requestUrl: getCurrencies().url, status: 'error', message, logLevel: ['error'] });
  const callbacks = useRef({ canSave, onSaved, onFinished });
  callbacks.current = { canSave, onSaved, onFinished };
  const queue = useRef<{ entries: { id: number; code: string; request: AxiosRequestConfig<CurrencyUpdatePayload> }[]; index: number; started: boolean } | null>(null);
  const submit = (rows: CurrencyUpdateValues[]) => {
    if (queue.current) return;
    if (!callbacks.current.canSave) {
      reportValidationError('Wait for currencies to load before saving.');
      return;
    }
    try {
      const entries = rows.map(row => ({ id: row.Currency?.Key, code: row.Currency?.Value, request: buildCurrencyRequest(row) }));
      if (!entries.length) return;
      queue.current = { entries, index: 0, started: false };
      setSaving(true);
      setRequest(entries[0].request);
    } catch (failure) {
      reportValidationError(failure instanceof Error ? failure.message : 'Unable to prepare currency updates.');
    }
  };

  useEffect(() => {
    const pending = queue.current;
    if (!pending) return;
    if (status === 'loading' || status === 'retry') {
      pending.started = true;
      return;
    }
    if (!pending.started || loading) return;
    const entry = pending.entries[pending.index];
    if (status === 'success' || status === 204) {
      pending.started = false;
      callbacks.current.onSaved(entry.id, entry.request.data);
      pending.index += 1;
      if (pending.index < pending.entries.length) {
        setRequest(pending.entries[pending.index].request);
        return;
      }
    } else if (status === 'error' || status === 'aborted' || typeof status === 'number' && status >= 400) {
      // The request hook already reports the API failure through MessageContext.
    } else {
      return;
    }
    queue.current = null;
    setSaving(false);
    callbacks.current.onFinished();
  }, [status, loading, setRequest]);

  return { submit, saving };
};
